import { useMsal } from "@azure/msal-react";
import { Button, Modal, Tooltip } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconCheck, IconClock, IconUserPlus } from "@tabler/icons-react";
import { MRT_Table, useMantineReactTable } from "mantine-react-table";
import type React from "react";
import { useEffect, useState } from "react";
import type { ChatVectorIndexOption } from "src/db";
import { useDatasources } from "src/hooks/useDatasources";
import { fetchPendingRequests } from "src/hooks/useFetchPendingRequests";
import type { DatasourceIngestionStatus } from "src/hooks/useIngestionStatus";
import DatasourceRequestAccessModal from "./DatasourceRequestAccessModal";

function getColumns(chatVectorIndex: ChatVectorIndexOption[]) {
    return [
        {
            header: "Access",
            enableSorting: false,
            size: 80,
            Cell: ({ row, table }) => {
                const [
                    accessRequestModalOpened,
                    { open: openAccessRequest, close: closeAccessRequest },
                ] = useDisclosure(false);
                const modalProps = table.options.meta as {
                    pendingRequests: Set<string>;
                    setPendingRequests: React.Dispatch<React.SetStateAction<Set<string>>>;
                };
                const isPending = modalProps?.pendingRequests?.has(row.original.id);

                if (row.original.has_access) {
                    return (
                        <Tooltip label="Access granted">
                            <div>
                                <Button
                                    aria-label="Access granted"
                                    size="xs"
                                    // compact
                                    style={{
                                        padding: "4px 8px",
                                        backgroundColor: "#107C10",
                                        border: "none",
                                    }}
                                    disabled
                                >
                                    <IconCheck size={12} style={{ color: "white" }} />
                                </Button>
                            </div>
                        </Tooltip>
                    );
                }

                if (isPending) {
                    return (
                        <Tooltip label="Access request pending">
                            <div>
                                <Button
                                    aria-label="Access request pending"
                                    size="xs"
                                    // compact
                                    style={{
                                        padding: "4px 8px",
                                        backgroundColor: "#f59e0b",
                                        border: "none",
                                    }}
                                    disabled
                                >
                                    <IconClock size={12} style={{ color: "white" }} />
                                </Button>
                            </div>
                        </Tooltip>
                    );
                }

                return (
                    <>
                        <Tooltip label="Request access to this data source">
                            <Button
                                aria-label="Request access"
                                size="xs"
                                // compact
                                onClick={openAccessRequest}
                                style={{ padding: "4px 8px" }}
                            >
                                <IconUserPlus size={12} />
                            </Button>
                        </Tooltip>
                        <DatasourceRequestAccessModal
                            opened={accessRequestModalOpened}
                            onClose={() => {
                                closeAccessRequest();
                            }}
                            onSubmit={() => {
                                modalProps?.setPendingRequests(
                                    (prev: Set<string>) => new Set([...prev, row.original.id]),
                                );
                            }}
                            title={`Request Access to ${
                                chatVectorIndex.find((index) => index.value === row.original.id)
                                    ?.label || row.original.id
                            }`}
                            requested_datasources={[row.original.id]}
                        />
                    </>
                );
            },
        },
        {
            id: "datasource_name",
            accessorKey: "id",
            accessorFn: (row) => {
                return (
                    chatVectorIndex.find((indexElement) => indexElement.value === row.id)?.label ??
                    row.id
                );
            },
            header: "Data Source",
        },
        {
            accessorFn: (row) => {
                return row.product_lines
                    .map((productLineInfo) => productLineInfo.product_line)
                    .toSorted((a, b) => a.localeCompare(b))
                    .join(", ");
            },
            header: "Product Lines",
            enableSorting: false,
        },
        {
            accessorFn: (row) => {
                return row.total_count > 0 ? row.total_count : "";
            },
            header: "Count",
            size: 60,
        },
        {
            accessorKey: "ingestion_status",
            header: "Ingestion Status",
        },
        {
            header: "Latest Refresh Date",
            accessorKey: "latest_document_timestamp",
            sortingFn: "datetime",
            accessorFn: (row) =>
                row.latest_document_timestamp
                    ? new Date(row.latest_document_timestamp * 1000)
                    : undefined,
            Cell: ({ cell }) => {
                const date = cell.getValue() as Date | null;

                return date
                    ? date.toLocaleDateString(undefined, {
                          day: "numeric",
                          month: "numeric",
                          year: "numeric",
                      })
                    : "";
            },
        },
    ];
}

interface IngestionStatusOverviewModalProps {
    status?: DatasourceIngestionStatus[];
    opened: boolean;
    fullScreen: boolean;
    onClose: () => void;
}

export const IngestionStatusOverviewModal: React.FC<IngestionStatusOverviewModalProps> = ({
    status,
    opened,
    fullScreen,
    onClose,
}) => {
    const { instance } = useMsal();
    const [pendingRequests, setPendingRequests] = useState<Set<string>>(new Set());
    const sortedValues = status?.toSorted((a, b) => a.id.localeCompare(b.id, "en"));
    const { data: chatVectorIndex = [] } = useDatasources();

    useEffect(() => {
        if (opened) {
            const fetchRequests = async () => {
                try {
                    setPendingRequests(await fetchPendingRequests(instance));
                } catch (err) {
                    console.error("Failed to fetch pending requests:", err);
                }
            };
            fetchRequests();
        }
    }, [instance, opened]);

    const table = useMantineReactTable({
        layoutMode: "grid",
        columns: getColumns(chatVectorIndex),
        data: sortedValues ?? [],
        meta: { pendingRequests, setPendingRequests },
        enablePagination: false,
        enableColumnActions: false,
        enableColumnFilters: false,
        initialState: {
            density: "xs",
            sorting: [{ id: "datasource_name", desc: false }],
        },
        mantineTableBodyCellProps: { style: { wordWrap: "break-word" } },
    });

    return (
        <Modal
            fullScreen={fullScreen}
            closeButtonProps={{ "aria-label": "Close modal" }}
            size="100em"
            opened={opened}
            onClose={onClose}
            title={"Data Source Details"}
        >
            <MRT_Table table={table} />
        </Modal>
    );
};
